import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Section } from "@astryxdesign/core/Section";
import { VStack } from "@astryxdesign/core/VStack";
import { HStack } from "@astryxdesign/core/HStack";
import { Text } from "@astryxdesign/core/Text";
import { Button } from "@astryxdesign/core/Button";
import { MoreMenu } from "@astryxdesign/core/MoreMenu";
import { IconButton } from "@astryxdesign/core/IconButton";
import { Token } from "@astryxdesign/core/Token";
import { Switch } from "@astryxdesign/core/Switch";
import { StatusDot } from "@astryxdesign/core/StatusDot";
import { Skeleton } from "@astryxdesign/core/Skeleton";
import { Table, pixel, proportional, type TableColumn } from "@astryxdesign/core/Table";
import { TextInput } from "@astryxdesign/core/TextInput";
import { AlertDialog } from "@astryxdesign/core/AlertDialog";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import { useToast } from "@astryxdesign/core/Toast";
import { Plus, Pencil, Trash2, KeyRound, Users as UsersIcon, Video, Search } from "lucide-react";
import { type CameraScope, type CreateUserInput, type UpdateUserInput, type User, api, isUnrestrictedScope } from "../../lib/api";
import { useAuth } from "../auth/AuthProvider";
import { PageHeader } from "../../components/PageHeader";
import { useIsNarrowViewport, useResponsiveTableDensity } from "../../lib/responsive";
import { RetryBanner } from "../../components/RetryBanner";
import { UserFormDialog } from "./UserFormDialog";
import { ResetPasswordDialog } from "./ResetPasswordDialog";
import { UserCameraAccessDialog } from "./UserCameraAccessDialog";

const LOADING_ROW_COUNT = 4;

// Table<T> requires T extends Record<string, unknown> — same wrapper
// pattern as CamerasPage's CameraRow, for the same reason: User itself has
// no index signature.
interface UserRow extends Record<string, unknown> {
  user: User;
}

export function UsersPage() {
  const tableDensity = useResponsiveTableDensity();
  const isNarrow = useIsNarrowViewport();
  const queryClient = useQueryClient();
  const showToast = useToast();
  const { user: me } = useAuth();

  const { data: users, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey: ["users"],
    queryFn: api.users.list,
  });

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const [resetTarget, setResetTarget] = useState<User | null>(null);
  const [accessTarget, setAccessTarget] = useState<User | null>(null);
  const [search, setSearch] = useState("");

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["users"] });

  const createMutation = useMutation({
    mutationFn: (input: CreateUserInput) => api.users.create(input),
    onSuccess: () => {
      invalidate();
      showToast({ body: "User added" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateUserInput }) => api.users.update(id, input),
    onSuccess: () => {
      invalidate();
      showToast({ body: "User updated" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.users.delete(id),
    onSuccess: () => {
      invalidate();
      showToast({ body: "User removed" });
      setDeleteTarget(null);
    },
    onError: (err) => {
      showToast({ body: err instanceof Error ? err.message : "Couldn't delete user", type: "error" });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ user, active }: { user: User; active: boolean }) =>
      active ? api.users.enable(user.id, user.version) : api.users.disable(user.id, user.version),
    onSuccess: invalidate,
    onError: (err) => {
      showToast({ body: err instanceof Error ? err.message : "Couldn't change account state", type: "error" });
    },
  });

  const cameraScopeMutation = useMutation({
    mutationFn: ({ id, cameraScope, version }: { id: string; cameraScope: CameraScope | null; version: number }) =>
      api.users.setCameraScope(id, cameraScope, version),
    onSuccess: () => {
      invalidate();
      showToast({ body: "Camera access updated" });
    },
  });

  const rows: UserRow[] = (users ?? []).map((user) => ({ user }));

  // Client-side, over data that's already fully loaded — see ProjectsPage's
  // own comment on why. Matches the fields the row itself shows: display
  // name, username, and email.
  const searchLower = search.trim().toLowerCase();
  const filteredRows = searchLower
    ? rows.filter(
        ({ user }) =>
          user.display_name.toLowerCase().includes(searchLower) ||
          user.username.toLowerCase().includes(searchLower) ||
          user.email.toLowerCase().includes(searchLower),
      )
    : rows;

  const openAddDialog = () => {
    setEditing(null);
    setFormOpen(true);
  };

  // The header (title, count, "Add user") renders unconditionally below —
  // only this body swaps between loading/error/empty/populated, so a slow
  // network or a failed request never takes the page's own frame with it.
  // Dropped whole on a phone: the role Token and the sign-in method are both
  // one short label, and they fold into the account cell without loss. Two
  // columns of chrome to carry them is what pushed the Active switch and the
  // three action buttons past the right edge of the viewport.
  const detailColumns: TableColumn<UserRow>[] = [
    {
      key: "role",
      header: "Role",
      width: pixel(110),
      renderCell: ({ user }) => (
        <Token
          color={user.role.permissions.includes("roles.manage") ? "purple" : "default"}
          label={user.role.name}
          size="sm"
        />
      ),
    },
    {
      key: "provider",
      header: "Sign-in method",
      width: pixel(140),
      renderCell: ({ user }) => (
        <Text type="supporting" color="secondary">
          {user.auth_provider === "local" ? "Password" : "SSO"}
        </Text>
      ),
    },
  ];

  let body: ReactNode;
  if (isLoading) {
    body = (
      <VStack gap={2}>
        {Array.from({ length: LOADING_ROW_COUNT }, (_, i) => (
          // eslint-disable-next-line react/no-array-index-key -- fixed-count static placeholders, never reordered
          <Skeleton key={i} height={44} radius={2} index={i} />
        ))}
      </VStack>
    );
  } else if (error) {
    body = (
      <RetryBanner
        title="Couldn't load users"
        description={error instanceof Error ? error.message : "Unknown error"}
        onRetry={() => void refetch()}
        isRetrying={isRefetching}
      />
    );
  } else if (rows.length === 0) {
    body = (
      <EmptyState
        icon={<UsersIcon size={32} />}
        title="No users yet"
        description="Add a teammate so they can sign in without sharing the admin account."
        actions={<Button label="Add user" variant="primary" icon={<Plus />} onClick={openAddDialog} />}
      />
    );
  } else {
    body = (
      <VStack gap={3}>
        <TextInput
          label="Search users"
          isLabelHidden
          placeholder="Search by name, username, or email"
          value={search}
          onChange={setSearch}
          startIcon={<Search size={16} />}
          hasClear
        />
        {filteredRows.length === 0 ? (
          <EmptyState icon={<Search size={32} />} title="No matching accounts" description={`No account matches "${search}".`} />
        ) : (
      <Table<UserRow>
        data={filteredRows}
        idKey={(row) => row.user.id}
        density={tableDensity}
        hasHover
        dividers="rows"
        columns={[
        {
          key: "name",
          header: "Account",
          width: proportional(2),
          renderCell: ({ user }) => (
            <HStack gap={1.5} vAlign="center">
              <StatusDot
                variant={user.is_active ? "success" : "neutral"}
                label={user.is_active ? "Active" : "Disabled"}
              />
              <VStack gap={0}>
                <HStack gap={1} vAlign="center">
                  <Text weight="medium">{user.display_name || user.username}</Text>
                  {user.id === me?.id && <Token label="You" size="sm" />}
                </HStack>
                <Text type="supporting" color="secondary">
                  {user.username} · {user.email}
                </Text>
                {/* Stands in for the two columns dropped on a phone. Which
                    role somebody holds is the whole point of this page, so it
                    follows the name down rather than disappearing with the
                    column that carried it. */}
                {isNarrow && (
                  <HStack gap={1} vAlign="center" wrap="wrap">
                    <Token
                      color={user.role.permissions.includes("roles.manage") ? "purple" : "default"}
                      label={user.role.name}
                      size="sm"
                    />
                    <Text type="supporting" color="secondary">
                      {user.auth_provider === "local" ? "Password" : "SSO"}
                    </Text>
                  </HStack>
                )}
              </VStack>
            </HStack>
          ),
        },
        ...(isNarrow ? [] : detailColumns),
        {
          key: "active",
          header: "Active",
          width: pixel(90),
          renderCell: ({ user }) => (
            <Switch
              label={`${user.is_active ? "Disable" : "Enable"} ${user.username}`}
              isLabelHidden
              value={user.is_active}
              isDisabled={user.id === me?.id}
              changeAction={async (active) => {
                await toggleMutation.mutateAsync({ user, active });
              }}
            />
          ),
        },
        {
          key: "actions",
          header: "",
          // Four icon buttons claim 160px of a 343px row on a phone, which
          // left the account cell ~110px: "Administrator" broke across two
          // lines, the email split mid-word, and the "You" token truncated to
          // "Y..". A menu is one 32px target instead, and the actions get
          // readable names rather than icons the row has no room to explain.
          width: isNarrow ? pixel(48) : pixel(160),
          renderCell: ({ user }) =>
            isNarrow ? (
              <HStack justify="end">
                <MoreMenu
                  label={`Actions for ${user.username}`}
                  items={[
                    { label: "Camera access", icon: <Video size={14} />, onClick: () => setAccessTarget(user) },
                    ...(user.auth_provider === "local"
                      ? [
                          {
                            label: "Reset password",
                            icon: <KeyRound size={14} />,
                            onClick: () => setResetTarget(user),
                          },
                        ]
                      : []),
                    {
                      label: "Edit",
                      icon: <Pencil size={14} />,
                      onClick: () => {
                        setEditing(user);
                        setFormOpen(true);
                      },
                    },
                    { type: "divider" as const },
                    {
                      label: "Delete",
                      icon: <Trash2 size={14} />,
                      variant: "destructive" as const,
                      // Deleting the account you are signed in as would sign
                      // you out mid-action; disabled here exactly as in the
                      // wide layout.
                      isDisabled: user.id === me?.id,
                      onClick: () => setDeleteTarget(user),
                    },
                  ]}
                />
              </HStack>
            ) : (
            <HStack gap={1} justify="end">
              <IconButton
                label={`Camera access for ${user.username}`}
                tooltip={
                  user.camera_scope_override && !isUnrestrictedScope(user.camera_scope_override)
                    ? "Camera access (restricted)"
                    : "Camera access"
                }
                icon={<Video />}
                variant="ghost"
                size="sm"
                onClick={() => setAccessTarget(user)}
              />
              {user.auth_provider === "local" && (
                <IconButton
                  label={`Reset password for ${user.username}`}
                  tooltip="Reset password"
                  icon={<KeyRound />}
                  variant="ghost"
                  size="sm"
                  onClick={() => setResetTarget(user)}
                />
              )}
              <IconButton
                label={`Edit ${user.username}`}
                tooltip="Edit"
                icon={<Pencil />}
                variant="ghost"
                size="sm"
                onClick={() => {
                  setEditing(user);
                  setFormOpen(true);
                }}
              />
              <IconButton
                label={`Delete ${user.username}`}
                tooltip="Delete"
                icon={<Trash2 />}
                variant="ghost"
                size="sm"
                isDisabled={user.id === me?.id}
                onClick={() => setDeleteTarget(user)}
              />
            </HStack>
            ),
        },
      ]}
      />
        )}
      </VStack>
    );
  }

  return (
    <Section padding={4}>
      <VStack gap={4}>
        <PageHeader
          title="Users"
          description={`${rows.length} account${rows.length === 1 ? "" : "s"}`}
          actions={<Button label="Add user" variant="primary" icon={<Plus />} onClick={openAddDialog} />}
        />
        {body}
      </VStack>

      <UserFormDialog
        isOpen={formOpen}
        onOpenChange={setFormOpen}
        editing={editing}
        isSaving={createMutation.isPending || updateMutation.isPending}
        onCreate={(input) => createMutation.mutateAsync(input)}
        onUpdate={(id, input) => updateMutation.mutateAsync({ id, input })}
      />

      <ResetPasswordDialog
        isOpen={resetTarget !== null}
        onOpenChange={(open) => !open && setResetTarget(null)}
        target={resetTarget}
      />

      <UserCameraAccessDialog
        isOpen={accessTarget !== null}
        onOpenChange={(open) => !open && setAccessTarget(null)}
        target={accessTarget}
        isSaving={cameraScopeMutation.isPending}
        onSave={(id, cameraScope, version) => cameraScopeMutation.mutateAsync({ id, cameraScope, version })}
      />

      <AlertDialog
        isOpen={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={`Delete ${deleteTarget?.username ?? "this user"}?`}
        description="This removes the account. They will no longer be able to sign in. This cannot be undone."
        actionLabel="Delete user"
        isActionLoading={deleteMutation.isPending}
        onAction={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
      />
    </Section>
  );
}
